import { useEffect, useState } from 'react';
import { AuthLayout } from '../components/AuthLayout';

export function CallbackPage() {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash.substring(1);
    const hashParams = new URLSearchParams(hash);

    // エラーチェック
    const error = params.get('error') ?? hashParams.get('error');
    const errorDescription = params.get('error_description') ?? hashParams.get('error_description');

    if (error) {
      setStatus('error');
      setErrorMessage(
        errorDescription
          ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
          : '認証がキャンセルされました',
      );
      return;
    }

    // トークンまたは認可コードを取得
    const code = params.get('code') ?? hashParams.get('code');
    const idToken = params.get('id_token') ?? hashParams.get('id_token');
    const accessToken = params.get('access_token') ?? hashParams.get('access_token');
    const refreshToken = params.get('refresh_token') ?? hashParams.get('refresh_token');
    const state = params.get('state') ?? hashParams.get('state');

    if (!code && !idToken) {
      setStatus('error');
      setErrorMessage('認可コードまたはトークンが見つかりません');
      return;
    }

    // 保存されたリダイレクトURIを取得
    const redirectUri =
      sessionStorage.getItem('auth_redirect_uri') ?? 'kyumo-ai-agent://auth/callback';

    const appUrl = new URL(redirectUri);
    if (code) appUrl.searchParams.set('code', code);
    if (idToken) appUrl.searchParams.set('id_token', idToken);
    if (accessToken) appUrl.searchParams.set('access_token', accessToken);
    if (refreshToken) appUrl.searchParams.set('refresh_token', refreshToken);
    if (state) appUrl.searchParams.set('state', state);

    // セッションストレージをクリア
    sessionStorage.removeItem('auth_state');
    sessionStorage.removeItem('auth_code_challenge');
    sessionStorage.removeItem('auth_redirect_uri');
    sessionStorage.removeItem('auth_client_id');

    // アプリにリダイレクト
    window.location.href = appUrl.toString();

    // リダイレクト後に成功画面を表示
    setStatus('success');
  }, []);

  if (status === 'error') {
    return (
      <AuthLayout title="認証エラー">
        <div className="space-y-6">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <p className="text-red-400">{errorMessage}</p>
          </div>
          <button
            type="button"
            onClick={() => window.close()}
            className="w-full py-3 px-4 bg-muted hover:bg-accent text-foreground font-medium rounded-lg transition-colors"
          >
            ウィンドウを閉じる
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (status === 'success') {
    return (
      <AuthLayout title="認証完了">
        <div className="space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                role="img"
                aria-label="成功"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
          <p className="text-center text-foreground">
            認証が完了しました。
            <br />
            アプリケーションに戻ってください。
          </p>
          <p className="text-center text-muted-foreground text-sm">
            このタブは手動で閉じてください
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="認証処理中...">
      <div className="space-y-6">
        <div className="flex justify-center">
          <div className="w-12 h-12 border-4 border-muted border-l-primary-500 rounded-full animate-spin" />
        </div>
        <p className="text-center text-muted-foreground">しばらくお待ちください...</p>
      </div>
    </AuthLayout>
  );
}
